import cx from 'classnames';
import NextLink from 'next/link';

import {AnimatedText, Button, Icon, Text} from '#component/base';
import {GetStarted} from '#component/page/LifestyleSummary/component';
import {
  LifestylePreviewCard,
  SubscriptionCTACard,
  SurveyCTACard,
  TallyExplorerCTACard,
} from '#component/partial';
import {Transition} from '#component/util';
import {headerTransition} from '#style/keyframes.css';
import {sprinkles} from '#style/sprinkles';
import {getNormalizedLifestyleScore} from '#util/lifestyleScore';

import {LifestyleEmptyProps} from './LifestyleEmpty.types';

export const CategoryItem = ({category}: {category: string}) => (
  <Text
    as="li"
    className={sprinkles({
      boxShadow: 'underline',
      content: 'between',
      flex: 'flex',
      items: 'center',
      pY: 16,
    })}
    variant="heading4"
    color="dustyGray">
    {`${category} score`}

    <Icon name="Lock" color="silver" />
  </Text>
);

export const LifestyleEmpty = ({
  categories = [],
  hasActiveSubscription = false,
  hasLabSubmission = false,
  registeredDate = '',
  surveyAssessment,
}: LifestyleEmptyProps) => {
  const lifestyleScore = getNormalizedLifestyleScore(
    surveyAssessment?.lifestyleScore || 0,
    surveyAssessment?.maxLifestyleScore || 100,
  );

  if (!hasLabSubmission) {
    return (
      <Transition appear show className={sprinkles({flex: 'flex', dir: 'col', gap: [32, 40, 48]})}>
        <div
          className={sprinkles({
            dir: ['col', 'col', 'row'],
            flex: 'flex',
            gap: [48, 56, 128],
            pB: [32, 72],
            pT: [56, 72, 128],
          })}>
          <div
            className={sprinkles({
              flex: 'flex',
              dir: 'col',
              gap: [24, 32, 40],
            })}>
            <AnimatedText color="deepBurgundy" variant="heading1">
              Unlock your Lifestyle Score
            </AnimatedText>

            <AnimatedText color="doveGray" variant="bodyLarge">
              Register your TallyAge™ test to unlock your Lifestyle Score, which will assess five
              key drivers of your health and aging.
            </AnimatedText>
          </div>

          <Transition.Child
            as="ul"
            className={cx(sprinkles({w: ['full', 'full', 'half']}), headerTransition)}>
            {categories.map((category, index) => (
              <CategoryItem key={index} category={category} />
            ))}
          </Transition.Child>
        </div>

        <Transition.Child className={headerTransition}>
          <GetStarted style={{maxWidth: 'auto', width: 'auto'}} />
        </Transition.Child>

        <Transition.Child className={headerTransition}>
          <TallyExplorerCTACard variant="lifestylesummary" href="/tallyexplorer/lifestylesummary" />
        </Transition.Child>
      </Transition>
    );
  }

  if (!surveyAssessment) {
    return (
      <Transition appear show className={sprinkles({flex: 'flex', dir: 'col', gap: [32, 40, 48]})}>
        <div
          className={sprinkles({
            dir: ['col', 'col', 'row'],
            flex: 'flex',
            gap: [48, 56, 128],
            pB: [32, 72],
            pT: [56, 72, 128],
          })}>
          <div
            className={sprinkles({
              flex: 'flex',
              dir: 'col',
              gap: [24, 32, 40],
            })}>
            <AnimatedText color="deepBurgundy" variant="heading1">
              Discover your Lifestyle Score
            </AnimatedText>

            <AnimatedText color="doveGray" variant="bodyLarge">
              Take our Lifestyle Survey to preview your Lifestyle Score and recommendations for five
              key drivers of your health and aging.
            </AnimatedText>

            <Transition.Child className={headerTransition}>
              <NextLink href="survey" passHref>
                <Button
                  as="a"
                  className={sprinkles({w: ['full', 'full', 'maxContent']})}
                  icon="ArrowRight">
                  Take the 5–min survey
                </Button>
              </NextLink>
            </Transition.Child>
          </div>

          <Transition.Child
            as="ul"
            className={cx(sprinkles({w: ['full', 'full', 'half']}), headerTransition)}>
            {categories.map((category, index) => (
              <CategoryItem key={index} category={category} />
            ))}
          </Transition.Child>
        </div>

        <Transition.Child className={headerTransition}>
          <SurveyCTACard />
        </Transition.Child>

        <Transition.Child className={headerTransition}>
          <TallyExplorerCTACard variant="lifestylesummary" href="/tallyexplorer/lifestylesummary" />
        </Transition.Child>
      </Transition>
    );
  }

  if (!hasActiveSubscription) {
    return (
      <Transition appear show className={sprinkles({flex: 'flex', dir: 'col', gap: [32, 40, 48]})}>
        <div
          className={sprinkles({
            flex: 'flex',
            dir: 'col',
            gap: [24, 32, 40],
            pB: [32, 72],
            pT: [56, 72, 128],
          })}>
          <AnimatedText color="deepBurgundy" variant="heading1">
            Unlock your full Lifestyle Summary
          </AnimatedText>

          <AnimatedText color="doveGray" variant="bodyLarge">
            View a preview of your overall Lifestyle Score from your recent Lifestyle Survey. Become
            a Tally Health™ member to receive personalized recommendations and Action Plans from our
            team of scientists.
          </AnimatedText>

          <Transition.Child className={headerTransition}>
            <NextLink href="/profile?tab=manage" passHref>
              <Button
                as="a"
                className={sprinkles({w: ['full', 'full', 'maxContent']})}
                icon="ArrowRight">
                Become a member
              </Button>
            </NextLink>
          </Transition.Child>
        </div>

        <Transition.Child className={headerTransition}>
          <LifestylePreviewCard
            hasActiveSubscription={hasActiveSubscription}
            lifestyleScore={lifestyleScore}
            registeredDate={registeredDate}
          />
        </Transition.Child>

        <Transition.Child className={headerTransition}>
          <SubscriptionCTACard href="/profile?tab=manage" />
        </Transition.Child>

        <Transition.Child className={headerTransition}>
          <TallyExplorerCTACard variant="lifestylesummary" href="/tallyexplorer/lifestylesummary" />
        </Transition.Child>
      </Transition>
    );
  }

  return (
    <Transition appear show className={sprinkles({flex: 'flex', dir: 'col', gap: [32, 40, 48]})}>
      <div
        className={sprinkles({
          flex: 'flex',
          dir: 'col',
          gap: [24, 32, 40],
          pB: [32, 72],
          pT: [56, 72, 128],
        })}>
        <AnimatedText color="deepBurgundy" variant="heading1">
          Your Lifestyle Score
        </AnimatedText>

        <AnimatedText color="doveGray" variant="bodyLarge">
          Preview your overall Lifestyle Score from your recent Lifestyle Survey.
        </AnimatedText>

        <AnimatedText color="doveGray" variant="bodyLarge">
          After you receive your TallyAge™ result, this summary will unlock additional content,
          including personalized recommendations and Action Plans.
        </AnimatedText>
      </div>

      <Transition.Child className={headerTransition}>
        <LifestylePreviewCard
          hasActiveSubscription={hasActiveSubscription}
          lifestyleScore={lifestyleScore}
          registeredDate={registeredDate}
        />
      </Transition.Child>

      <Transition.Child className={headerTransition}>
        <TallyExplorerCTACard variant="lifestylesummary" href="/tallyexplorer/lifestylesummary" />
      </Transition.Child>
    </Transition>
  );
};
